import { useRef, useState } from "react";
import type { ClipboardEvent, ChangeEvent } from "react";
import { attachmentsApi } from "../../api/attachments.api";

export type AttachedFile = {
  id: string;
  name: string;
  previewUrl?: string;
  isImage: boolean;
  uploading: boolean;
  error: boolean;
  errorMsg?: string;
  url?: string;
};

const ACCEPTED_TYPES = [
  "image/png", "image/jpeg", "image/gif", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain", "text/csv",
  "application/zip", "application/x-zip-compressed",
].join(",");

const IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

function uid() {
  return Math.random().toString(36).slice(2);
}

async function compressImage(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d")!.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg", 0.85).split(",")[1]);
    };
    img.src = dataUrl;
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useAttachments() {
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const anyUploading = files.some((f) => f.uploading);

  async function processFile(file: File) {
    const id = uid();
    const isImage = IMAGE_TYPES.has(file.type);
    const previewUrl = isImage ? await fileToDataUrl(file) : undefined;

    setFiles((prev) => [
      ...prev,
      { id, name: file.name, previewUrl, isImage, uploading: true, error: false },
    ]);

    try {
      let base64: string;
      let contentType: string;
      if (isImage) {
        base64 = await compressImage(previewUrl!);
        contentType = "image/jpeg";
      } else {
        base64 = await fileToBase64(file);
        contentType = file.type;
      }

      const result = await attachmentsApi.upload(base64, contentType, file.name);
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, uploading: false, url: result.url } : f)),
      );
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : "Upload failed");
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, uploading: false, error: true, errorMsg: msg } : f)),
      );
    }
  }

  async function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const imageItem = Array.from(e.clipboardData.items).find(
      (item) => item.kind === "file" && item.type.startsWith("image/"),
    );
    if (!imageItem) return;
    e.preventDefault();
    const file = imageItem.getAsFile();
    if (!file) return;
    await processFile(new File([file], `screenshot-${Date.now()}.jpg`, { type: file.type }));
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function buildMarkdown(body: string): string {
    const uploaded = files.filter((f) => f.url);
    if (uploaded.length === 0) return body;
    const links = uploaded
      .map((f) => (f.isImage ? `![${f.name}](${f.url})` : `[${f.name}](${f.url})`))
      .join("\n");
    return body + "\n\n" + links;
  }

  function reset() {
    setFiles([]);
  }

  return { files, setFiles, anyUploading, handlePaste, processFile, removeFile, buildMarkdown, reset };
}

interface PanelProps {
  files: AttachedFile[];
  setFiles: React.Dispatch<React.SetStateAction<AttachedFile[]>>;
  anyUploading: boolean;
  processFile: (file: File) => Promise<void>;
  removeFile: (id: string) => void;
}

export function AttachmentsPanel({ files, setFiles: _setFiles, anyUploading, processFile, removeFile }: PanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    for (const file of picked) {
      await processFile(file);
    }
  }

  return (
    <>
      {files.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          {files.map((f) =>
            f.isImage ? (
              <div key={f.id} style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={f.previewUrl}
                  alt={f.name}
                  title={f.name}
                  onClick={() => !f.uploading && !f.error && setLightboxSrc(f.previewUrl!)}
                  style={{
                    height: 72, maxWidth: 128, objectFit: "cover",
                    borderRadius: 4, border: "1px solid var(--border, #444)",
                    opacity: f.uploading ? 0.5 : 1,
                    cursor: !f.uploading && !f.error ? "zoom-in" : "default",
                  }}
                />
                {!f.uploading && (
                  <button type="button" onClick={() => removeFile(f.id)} style={removeBtnStyle} aria-label="Remove">×</button>
                )}
                {f.uploading && <OverlayLabel>Uploading…</OverlayLabel>}
                {f.error && <OverlayLabel error errorMsg={f.errorMsg} />}
              </div>
            ) : (
              <div key={f.id} style={{
                position: "relative", display: "flex", alignItems: "center", gap: 6,
                padding: "4px 28px 4px 8px",
                borderRadius: 4, border: "1px solid var(--border, #444)",
                background: "var(--surface2, rgba(255,255,255,0.05))",
                fontSize: 13, maxWidth: 240,
                opacity: f.uploading ? 0.6 : 1,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>📎</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={f.name}>
                  {f.name}
                </span>
                {f.uploading && <span style={{ fontSize: 11, opacity: 0.7, flexShrink: 0 }}>uploading…</span>}
                {f.error && (
                  <span style={{ fontSize: 11, color: "#fc8181", flexShrink: 0 }} title={f.errorMsg}>failed</span>
                )}
                {!f.uploading && (
                  <button type="button" onClick={() => removeFile(f.id)} style={removeBtnStyle} aria-label="Remove">×</button>
                )}
              </div>
            ),
          )}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          {anyUploading ? "Uploading…" : "Paste a screenshot (Ctrl+V / ⌘V) or"}
        </p>
        {!anyUploading && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: "none", border: "1px solid var(--border, #555)",
                borderRadius: 4, padding: "2px 10px", fontSize: 13,
                cursor: "pointer", color: "inherit", whiteSpace: "nowrap",
              }}
            >
              📎 Attach file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </>
        )}
      </div>

      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxSrc}
            alt="Preview"
            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 6, boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            style={{ position: "fixed", top: 16, right: 20, background: "none", border: "none", color: "#fff", fontSize: 32, lineHeight: 1, cursor: "pointer", padding: 0 }}
            aria-label="Close"
          >×</button>
        </div>
      )}
    </>
  );
}

const removeBtnStyle: React.CSSProperties = {
  position: "absolute", top: -6, right: -6,
  width: 18, height: 18, borderRadius: "50%",
  background: "#e53e3e", border: "none", color: "#fff",
  fontSize: 11, lineHeight: "18px", textAlign: "center",
  cursor: "pointer", padding: 0,
};

function OverlayLabel({ children, error, errorMsg }: { children?: React.ReactNode; error?: boolean; errorMsg?: string }) {
  return (
    <span style={{
      position: "absolute", inset: 0, display: "flex",
      alignItems: "center", justifyContent: "center", flexDirection: "column",
      fontSize: 11, color: "#fff",
      background: error ? "rgba(180,0,0,0.75)" : "rgba(0,0,0,0.45)",
      borderRadius: 4, padding: 4, textAlign: "center",
    }}>
      {error ? (
        <>
          <span>Failed</span>
          {errorMsg && <span style={{ fontSize: 10, marginTop: 2, opacity: 0.9 }}>{errorMsg}</span>}
        </>
      ) : children}
    </span>
  );
}
